import { useDroppable } from '@dnd-kit/core';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import { CSSProperties, useMemo } from 'react';

import { JobCard, JobPlacement } from '~pages/Dispatch/components/drivers/JobCard';
import { useStores } from '~store';
import { hexToRgba } from '~utils/utilFunctions';

import { INCREMENTS_PER_HOUR } from './constants';

interface DroppableCellProps {
  assigneeId: string;
  columnIndex: number;
  style: CSSProperties;
}

export const DroppableCell = observer(
  ({ assigneeId, columnIndex, style }: DroppableCellProps) => {
    const theme = useTheme();
    const { driverSchedulerStore } = useStores();
    const offsetColumnIndex = useMemo(() => columnIndex, [columnIndex]);
    const id = `${assigneeId}:${offsetColumnIndex}`;

    const { setNodeRef, isOver } = useDroppable({ id });

    const isWholeHourColumn = offsetColumnIndex % INCREMENTS_PER_HOUR === 0;
    const verticalBorderColor = isWholeHourColumn
      ? theme.brandV2.colors.treadGray7
      : 'transparent';

    const slotKey = `${assigneeId}:${offsetColumnIndex}`;
    const stackedJobs = driverSchedulerStore.jobStacks[slotKey] || [];
    const stackedJobsCount = stackedJobs.length;
    const assignedJobId = driverSchedulerStore.jobAssignments[slotKey]?.id;

    return (
      <Box
        ref={setNodeRef}
        style={{
          ...style,
          borderTop: 'none',
          borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
          borderLeft: `1px solid ${verticalBorderColor}`,
          borderRight: 'none',
          backgroundColor: isOver
            ? hexToRgba(theme.brandV2.colors.treadYellow, 0.25)
            : theme.semantic.surfaceRaised,
          boxShadow: isOver
            ? `inset 0 0 0 2px ${theme.brandV2.colors.treadYellow}`
            : 'none',
          transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        }}
        data-ui="page-dispatch__droppable-cell__interactive"
      >
        {stackedJobs.length > 0
          ? stackedJobs.map((stackedJob) => (
              <JobCard
                key={stackedJob.id}
                jobId={stackedJob.id}
                style={{ position: 'absolute', top: 0, left: 0 }}
                jobPlacement={JobPlacement.DRIVERS_GRID}
                stackedJobsCount={stackedJobsCount}
                slotKey={slotKey}
              />
            ))
          : assignedJobId && (
              <JobCard
                jobId={assignedJobId}
                style={{ position: 'absolute', top: 0, left: 0 }}
                jobPlacement={JobPlacement.DRIVERS_GRID}
                stackedJobsCount={1}
              />
            )}
      </Box>
    );
  },
);
